/**
 * Fault Details View — Read-Only
 *
 * Comprehensive read-only fault detail modal showing every piece of information:
 * - Header: customer, service, device, severity, stage, SLA
 * - Intake details
 * - Remote logs / diagnostics
 * - Call notes
 * - Client communications
 * - Investigation history (full timeline)
 * - Tech consult response
 * - Deployment job summary
 * - Cost tracking
 * - Resolution details
 * - Related / repeat faults
 *
 * Used in /services and /customers to drill into fault history.
 * Data follows the service — if a service transfers to a different customer,
 * the fault history (linked by serviceId) travels with it.
 */

import { getLocaleSync } from '@/utils/locale';
import { X, Clock, MapPin, Wifi, AlertTriangle, Phone, Mail, MessageSquare, Wrench,
  Repeat, DollarSign, Truck, CheckCircle, ChevronDown, ChevronRight, Monitor,
  FileText, User, Activity, Zap, Camera, ClipboardList,
} from 'lucide-react';
import { useState } from 'react';
import type { JobCompletionReasoning } from '@/types/schema';
import type { Fault, RemoteLog, CallNote, ClientUpdate, InvestigationHistoryEntry } from '@/types/fault.types';
import { FAULT_STAGE_LABELS, FAULT_STAGE_COLORS, migrateFaultStage } from '@/types/fault.types';
import { MarkdownDisplay } from '@/components/MarkdownDisplay';
import {
  PART_STATUS_LABEL,
  visitReportFromDeploymentSummary,
  type VisitReport,
} from '@/services/visit-report.service';
import { formatCurrency } from '@/utils/pricing';

/**
 * The AI's write-up of the tech's field log.
 *
 * Was a two-way toggle between a technical report and a customer summary. The
 * customer summary is AI copy for the post-job email — billing correspondence,
 * not a record of the fault — so it no longer appears here at all. What's left
 * only exists on Fault / Maintenance visits that actually kept a field log
 * (`ai-summary.service.ts`); on everything else the tech's raw note above IS
 * the report. Collapsed by default: the log is the record, this is a read of it.
 */
function DeploymentAiWriteUp({ technicalReport }: { technicalReport?: string }) {
  const [open, setOpen] = useState(false);
  if (!technicalReport?.trim()) return null;

  return (
    <div className="mt-2 rounded bg-white/5 p-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors"
      >
        <FileText className="w-3 h-3" />
        AI write-up of the log
        <span className="ml-auto font-normal normal-case tracking-normal">
          {open ? 'Hide' : 'Show'}
        </span>
      </button>
      {open && (
        <div className="mt-1.5 text-slate-300">
          <MarkdownDisplay compact content={technicalReport} />
        </div>
      )}
    </div>
  );
}

/** Gear the tech confirmed using on the callout, plus anything grabbed on site. */
function DeploymentParts({ report }: { report: VisitReport }) {
  if (report.parts.length === 0) return null;
  return (
    <div className="mt-2 rounded bg-white/5 p-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
        Parts used
      </p>
      <ul className="space-y-0.5">
        {report.parts.map((part) => (
          <li key={part.key} className="flex items-baseline gap-1.5 text-slate-300">
            <CheckCircle className="w-3 h-3 text-emerald-400 shrink-0 self-center" />
            <span>{part.name}</span>
            {part.usedQty != null && (
              <span className="font-semibold text-slate-200 tabular-nums">
                x{part.usedQty}{part.unit ? ` ${part.unit}` : ''}
              </span>
            )}
            {part.status === 'extra' && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-400">
                {PART_STATUS_LABEL.extra}
              </span>
            )}
            {part.note && <span className="text-[10px] text-slate-500">{part.note}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Tech's completion reasoning synced from the deployment job — surfaces the
 * diagnosis ("what fixed it and why") on the fault record instead of forcing
 * the office to open the job.
 */
const REASONING_LABELS: Record<string, string> = {
  whatAndWhy: 'Tech read on this job',
  symptomProgression: 'Symptom progression',
  ruledOutPaths: 'Ruled out',
  whatFixedIt: 'What fixed it',
  keyDecisions: 'Key decisions',
  siteSurprises: 'Site surprises',
  customerContext: 'Customer context',
  scopeRationale: 'Scope rationale',
  risksConcerns: 'Risks / concerns',
  siteQuirks: 'Site quirks',
  anythingNotable: 'Notable',
};

function DeploymentReasoning({ reasoning }: { reasoning?: JobCompletionReasoning }) {
  if (!reasoning) return null;
  const r = reasoning as unknown as Record<string, unknown>;
  const items = Object.entries(REASONING_LABELS)
    .filter(([key]) => typeof r[key] === 'string' && (r[key] as string).trim())
    .map(([key, label]) => ({ label, value: r[key] as string }));
  if (items.length === 0) return null;
  return (
    <div className="mt-2 bg-indigo-500/5 border border-indigo-500/20 rounded p-2 space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400">
        Tech Reasoning
      </p>
      {items.map(({ label, value }) => (
        <div key={label} className="text-xs">
          <span className="font-semibold text-slate-300">{label}: </span>
          <span className="text-slate-200 whitespace-pre-wrap">{value}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString(getLocaleSync(), { hour: 'numeric', minute: '2-digit' });
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDuration(minutes?: number): string {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatSeconds(seconds?: number): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function severityColor(severity: string) {
  switch (severity) {
    case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
    default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}

// ---------------------------------------------------------------------------
// Collapsible Section
// ---------------------------------------------------------------------------
function Section({ title, icon, count, defaultOpen = true, children, accentColor = 'text-slate-400' }: {
  title: string;
  icon: React.ReactNode;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  accentColor?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-white/5 transition-colors text-left"
      >
        <span className={accentColor}>{icon}</span>
        <span className="text-white font-semibold text-sm flex-1">{title}</span>
        {count !== undefined && (
          <span className="text-xs text-slate-400 bg-white/10 px-2 py-0.5 rounded-full">{count}</span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline Entry (generic)
// ---------------------------------------------------------------------------
function TimelineEntry({ timestamp, author, label, labelColor, children }: {
  timestamp: string;
  author: string;
  label?: string;
  labelColor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative pl-5 pb-3 border-l-2 border-white/10 last:border-l-0 last:pb-0">
      <div className="absolute left-[-5px] top-1 w-2 h-2 rounded-full bg-white/30" />
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-[11px] text-slate-500">{formatDateTime(timestamp)}</span>
        <span className="text-[11px] text-slate-400">by {author}</span>
        {label && (
          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${labelColor || 'bg-white/10 text-slate-300'}`}>
            {label}
          </span>
        )}
      </div>
      <div className="text-sm text-slate-200">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface FaultDetailsViewProps {
  fault: Fault;
  isOpen: boolean;
  onClose: () => void;
}

export function FaultDetailsView({ fault, isOpen, onClose }: FaultDetailsViewProps) {
  if (!isOpen || !fault) return null;

  const stage = migrateFaultStage(fault.stage);
  const stageColors = FAULT_STAGE_COLORS[stage];
  const stageLabel = FAULT_STAGE_LABELS[stage];

  // Merge all events into a single timeline sorted by timestamp
  type TimelineEventData = InvestigationHistoryEntry | CallNote | RemoteLog | ClientUpdate;
  const allEvents: { timestamp: string; type: string; data: TimelineEventData }[] = [];

  (fault.investigationHistory || []).forEach((e) => {
    allEvents.push({ timestamp: e.timestamp, type: 'investigation', data: e });
  });
  (fault.callNotes || []).forEach((e) => {
    allEvents.push({ timestamp: e.timestamp, type: 'call', data: e });
  });
  (fault.remoteLogs || []).forEach((e) => {
    allEvents.push({ timestamp: e.timestamp, type: 'remote', data: e });
  });
  (fault.clientUpdates || []).forEach((e) => {
    allEvents.push({ timestamp: e.timestamp, type: 'client_update', data: e });
  });

  allEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return (
    // z-[460] (was z-[300]) so this nested view sits above CustomerDetailModal
    // even when the customer is provider-mounted at z-[400] (Customer 360
    // cross-entity flow). Stays above JobDetailModal (z-[450]) to preserve
    // the prior relative ordering inside the customer modal.
    <div className="fixed inset-0 bg-black/80 z-[460] flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-white/10 rounded-2xl w-full max-w-3xl my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h2 className="text-xl font-bold text-white">{fault.clientName}</h2>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${severityColor(fault.severity)}`}>
                  {fault.severity.toUpperCase()}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${stageColors.badge}`}>
                  {stageLabel}
                </span>
                {fault.archived && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-500/20 text-slate-400 border border-slate-500/30">
                    {fault.resolutionType === 'deployed'
                      ? fault.deploymentJobCompleted ? 'Completed' : 'Deployed'
                      : fault.resolutionType === 'resolved_remotely' ? 'Resolved Remotely'
                      : fault.resolutionType === 'closed_duplicate' ? 'Closed — Duplicate'
                      : fault.resolutionType === 'closed_no_fault_found' ? 'Closed — No Fault Found'
                      : 'Archived'}
                  </span>
                )}
                {fault.repeatFlag && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">
                    <Repeat className="w-3 h-3 inline mr-1" />
                    Repeat x{fault.repeatCount || 2}
                  </span>
                )}
              </div>

              <p className="text-slate-300 text-sm mb-2">{fault.symptom}</p>

              {fault.address && (
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span>{fault.address}</span>
                </div>
              )}
            </div>

            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-4 mt-3 flex-wrap text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Created: {formatDateTime(fault.createdAt)}
            </span>
            {fault.intakeMethod && (
              <span>Intake: {fault.intakeMethod}</span>
            )}
            {fault.intakeBy && (
              <span>By: {fault.intakeBy}</span>
            )}
            {fault.callDuration && (
              <span>Call: {formatSeconds(fault.callDuration)}</span>
            )}
          </div>
        </div>

        {/* ─── Body ─── */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">

          {/* Service & Device Info */}
          {(fault.serviceType || fault.deviceInfo) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fault.serviceType && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Service</div>
                  <div className="text-white font-semibold text-sm flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-blue-400" />
                    {fault.serviceType}
                    {fault.servicePlanName && <span className="text-slate-400 font-normal">• {fault.servicePlanName}</span>}
                  </div>
                  {fault.serviceMonthlyPrice && (
                    <div className="text-xs text-slate-400 mt-1">{formatCurrency(fault.serviceMonthlyPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</div>
                  )}
                </div>
              )}
              {fault.deviceInfo && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Device</div>
                  <div className="text-white font-semibold text-sm flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-purple-400" />
                    {fault.deviceInfo.type}
                    {fault.deviceInfo.model && <span className="text-slate-400 font-normal">• {fault.deviceInfo.model}</span>}
                  </div>
                  {fault.deviceInfo.serialNumber && (
                    <div className="text-xs text-slate-400 mt-1 font-mono">S/N: {fault.deviceInfo.serialNumber}</div>
                  )}
                  {fault.deviceInfo.location && (
                    <div className="text-xs text-slate-400 mt-0.5">Location: {fault.deviceInfo.location}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SLA Status */}
          {(fault.slaResponseDeadline || fault.slaResolutionDeadline) && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">SLA Tracking</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-slate-400 mb-0.5">Response</div>
                  <div className="text-white">{formatDateTime(fault.slaResponseDeadline)}</div>
                  {fault.slaResponseMet !== undefined && (
                    <span className={`inline-flex items-center gap-1 mt-1 ${fault.slaResponseMet ? 'text-emerald-400' : 'text-red-400'}`}>
                      {fault.slaResponseMet ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {fault.slaResponseMet ? 'Met' : 'Breached'}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-slate-400 mb-0.5">Resolution</div>
                  <div className="text-white">{formatDateTime(fault.slaResolutionDeadline)}</div>
                  {fault.slaResolutionMet !== undefined && (
                    <span className={`inline-flex items-center gap-1 mt-1 ${fault.slaResolutionMet ? 'text-emerald-400' : 'text-red-400'}`}>
                      {fault.slaResolutionMet ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {fault.slaResolutionMet ? 'Met' : 'Breached'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Intake Notes */}
          {fault.intakeNotes && (
            <Section title="Intake Notes" icon={<FileText className="w-4 h-4" />} accentColor="text-sky-400">
              <div className="text-sm text-slate-200 whitespace-pre-wrap bg-white/5 rounded p-3">
                {fault.intakeNotes}
              </div>
            </Section>
          )}

          {/* Remote Logs / Diagnostics */}
          {fault.remoteLogs?.length > 0 && (
            <Section
              title="Remote Logs / Diagnostics"
              icon={<Monitor className="w-4 h-4" />}
              count={fault.remoteLogs.length}
              accentColor="text-blue-400"
            >
              {fault.remoteLogs
                .slice()
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map((log: RemoteLog) => (
                  <TimelineEntry
                    key={log.id}
                    timestamp={log.timestamp}
                    author={log.author}
                    label={log.action}
                    labelColor={
                      log.action === 'diagnostic' ? 'bg-blue-500/20 text-blue-300' :
                      log.action === 'reboot' ? 'bg-amber-500/20 text-amber-300' :
                      log.action === 'configuration_change' ? 'bg-purple-500/20 text-purple-300' :
                      'bg-white/10 text-slate-300'
                    }
                  >
                    <p className="whitespace-pre-wrap">{log.notes}</p>
                  </TimelineEntry>
                ))}
            </Section>
          )}

          {/* Call Notes */}
          {fault.callNotes?.length > 0 && (
            <Section
              title="Call Notes"
              icon={<Phone className="w-4 h-4" />}
              count={fault.callNotes.length}
              accentColor="text-amber-400"
            >
              {fault.callNotes
                .slice()
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map((note: CallNote) => (
                  <TimelineEntry key={note.id} timestamp={note.timestamp} author={note.author}>
                    <p className="whitespace-pre-wrap">{note.notes}</p>
                    {note.callDuration && (
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Duration: {note.callDuration}m
                      </div>
                    )}
                  </TimelineEntry>
                ))}
            </Section>
          )}

          {/* Client Communications */}
          {fault.clientUpdates?.length > 0 && (
            <Section
              title="Client Communications"
              icon={<MessageSquare className="w-4 h-4" />}
              count={fault.clientUpdates.length}
              accentColor="text-cyan-400"
            >
              {fault.clientUpdates
                .slice()
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map((update: ClientUpdate) => (
                  <TimelineEntry
                    key={update.id}
                    timestamp={update.timestamp}
                    author={update.author}
                    label={update.method}
                    labelColor={
                      update.method === 'phone' ? 'bg-emerald-500/20 text-emerald-300' :
                      update.method === 'sms' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-purple-500/20 text-purple-300'
                    }
                  >
                    <p className="whitespace-pre-wrap">{update.content}</p>
                    {update.nextUpdateDue && (
                      <div className="text-xs text-slate-400 mt-1">
                        Next update due: {formatDateTime(update.nextUpdateDue)}
                      </div>
                    )}
                  </TimelineEntry>
                ))}
            </Section>
          )}

          {/* Tech Consult Response */}
          {fault.lastConsultResponse && (
            <Section title="Tech Consult Response" icon={<Wrench className="w-4 h-4" />} accentColor="text-emerald-400">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <User className="w-3 h-3" />
                  <span className="text-emerald-300 font-semibold">{fault.lastConsultResponse.techName}</span>
                  <span>•</span>
                  <span>{formatDateTime(fault.lastConsultResponse.completedAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                    fault.lastConsultResponse.recommendation === 'deploy' ? 'bg-purple-500/20 text-purple-300' :
                    fault.lastConsultResponse.recommendation === 'resolved_remotely' ? 'bg-emerald-500/20 text-emerald-300' :
                    fault.lastConsultResponse.recommendation === 'escalate' ? 'bg-red-500/20 text-red-300' :
                    'bg-blue-500/20 text-blue-300'
                  }`}>
                    {fault.lastConsultResponse.recommendation.replace(/_/g, ' ')}
                  </span>
                  {fault.lastConsultResponse.estimatedDuration && (
                    <span className="text-xs text-slate-400">
                      Est: {formatDuration(fault.lastConsultResponse.estimatedDuration)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-200 whitespace-pre-wrap">{fault.lastConsultResponse.notes}</p>
              </div>
            </Section>
          )}

          {/* Full Investigation Timeline */}
          {allEvents.length > 0 && (
            <Section
              title="Full Timeline"
              icon={<Activity className="w-4 h-4" />}
              count={allEvents.length}
              defaultOpen={false}
              accentColor="text-white"
            >
              {allEvents.map((event, idx) => {
                if (event.type === 'investigation') {
                  const e = event.data as InvestigationHistoryEntry;
                  const eStage = migrateFaultStage(e.stage);
                  return (
                    <TimelineEntry key={`inv-${e.id}-${idx}`} timestamp={e.timestamp} author={e.author}
                      label={FAULT_STAGE_LABELS[eStage] || e.stage}
                      labelColor={FAULT_STAGE_COLORS[eStage]?.badge || 'bg-white/10 text-slate-300'}
                    >
                      <p className="font-medium text-white/80">{e.action}</p>
                      {e.details && <p className="text-slate-300 whitespace-pre-wrap">{e.details}</p>}
                    </TimelineEntry>
                  );
                }
                if (event.type === 'call') {
                  const e = event.data as CallNote;
                  return (
                    <TimelineEntry key={`call-${e.id}-${idx}`} timestamp={e.timestamp} author={e.author}
                      label="Call" labelColor="bg-amber-500/20 text-amber-300"
                    >
                      <p className="whitespace-pre-wrap">{e.notes}</p>
                      {e.callDuration && <span className="text-xs text-slate-400">Duration: {e.callDuration}m</span>}
                    </TimelineEntry>
                  );
                }
                if (event.type === 'remote') {
                  const e = event.data as RemoteLog;
                  return (
                    <TimelineEntry key={`remote-${e.id}-${idx}`} timestamp={e.timestamp} author={e.author}
                      label={e.action} labelColor="bg-blue-500/20 text-blue-300"
                    >
                      <p className="whitespace-pre-wrap">{e.notes}</p>
                    </TimelineEntry>
                  );
                }
                if (event.type === 'client_update') {
                  const e = event.data as ClientUpdate;
                  return (
                    <TimelineEntry key={`cu-${e.id}-${idx}`} timestamp={e.timestamp} author={e.author}
                      label={`Client: ${e.method}`} labelColor="bg-cyan-500/20 text-cyan-300"
                    >
                      <p className="whitespace-pre-wrap">{e.content}</p>
                    </TimelineEntry>
                  );
                }
                return null;
              })}
            </Section>
          )}

          {/* Deployment Job Summary */}
          {fault.deploymentJobId && (
            <Section title="Deployment" icon={<Truck className="w-4 h-4" />} accentColor="text-purple-400">
              <div className="space-y-2">
                <div className="text-xs text-slate-400">
                  Job ID: <span className="font-mono text-white/60">{fault.deploymentJobId}</span>
                </div>
                {fault.deploymentJobCompleted ? (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                      <CheckCircle className="w-4 h-4" />
                      Job Completed
                    </div>
                    {fault.deploymentJobSummary && (
                      <div className="space-y-1 text-xs">
                        {fault.deploymentJobSummary.techName && (
                          <div className="text-slate-300">
                            <User className="w-3 h-3 inline mr-1" />
                            Tech: {fault.deploymentJobSummary.techName}
                          </div>
                        )}
                        {fault.deploymentJobSummary.actualDuration && (
                          <div className="text-slate-300">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Duration: {formatDuration(fault.deploymentJobSummary.actualDuration)}
                          </div>
                        )}
                        {fault.deploymentJobSummary.fieldLogCount != null && (
                          <div className="text-slate-300">
                            <ClipboardList className="w-3 h-3 inline mr-1" />
                            Field log entries: {fault.deploymentJobSummary.fieldLogCount}
                          </div>
                        )}
                        {fault.deploymentJobSummary.photoCount != null && (
                          <div className="text-slate-300">
                            <Camera className="w-3 h-3 inline mr-1" />
                            Photos: {fault.deploymentJobSummary.photoCount}
                          </div>
                        )}
                        {/* The tech's note is now ALWAYS shown, not just as a
                            fallback when the AI produced nothing. Raw notes are
                            the record; the AI read of the log sits under them. */}
                        {fault.deploymentJobSummary.completionNote && (
                          <div className="mt-2 bg-white/5 rounded p-2">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                              Tech&apos;s note
                            </p>
                            <p className="text-slate-200 whitespace-pre-wrap">
                              {fault.deploymentJobSummary.completionNote}
                            </p>
                          </div>
                        )}
                        <DeploymentParts
                          report={visitReportFromDeploymentSummary(fault.deploymentJobSummary)}
                        />
                        <DeploymentAiWriteUp
                          technicalReport={fault.deploymentJobSummary.technicalReport}
                        />
                        {fault.deploymentJobSummary.needsReturnVisit && (
                          <div className="mt-2 flex items-center gap-1 text-amber-400">
                            <AlertTriangle className="w-3 h-3" />
                            Return visit needed: {fault.deploymentJobSummary.returnVisitReason || 'No reason given'}
                          </div>
                        )}
                        <DeploymentReasoning reasoning={fault.deploymentJobSummary.completionReasoning} />
                      </div>
                    )}
                    {fault.deploymentJobCompletedAt && (
                      <div className="text-xs text-slate-400 mt-1">
                        Completed: {formatDateTime(fault.deploymentJobCompletedAt)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-purple-400 text-sm font-semibold">
                      <Truck className="w-4 h-4 animate-pulse" />
                      In Field
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Cost Tracking */}
          {fault.costs && fault.costs.totalCost > 0 && (
            <Section title="Cost Tracking" icon={<DollarSign className="w-4 h-4" />} accentColor="text-emerald-400">
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Labour</div>
                    <div className="text-white font-bold">{formatCurrency(fault.costs.labourCost, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className="text-[10px] text-slate-400">{formatDuration(fault.costs.labourMinutes)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Materials</div>
                    <div className="text-white font-bold">{formatCurrency(fault.costs.materialsCost, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Total</div>
                    <div className="text-emerald-400 font-bold text-lg">{formatCurrency(fault.costs.totalCost, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                </div>

                {fault.costs.materials?.length > 0 && (
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Materials Used</div>
                    {fault.costs.materials.map((mat, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-slate-300 py-0.5">
                        <span>{mat.description} x{mat.quantity}</span>
                        <span className="text-slate-400">{formatCurrency((mat.quantity * mat.unitCost), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Resolution */}
          {fault.resolutionType && (
            <Section title="Resolution" icon={<CheckCircle className="w-4 h-4" />} accentColor="text-emerald-400">
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-emerald-500/20 text-emerald-300">
                    {fault.resolutionType.replace(/_/g, ' ')}
                  </span>
                  {fault.resolvedAt && (
                    <span className="text-xs text-slate-400">{formatDateTime(fault.resolvedAt)}</span>
                  )}
                  {fault.resolvedBy && (
                    <span className="text-xs text-slate-400">by {fault.resolvedBy}</span>
                  )}
                </div>
                {fault.resolutionNotes && (
                  <p className="text-sm text-slate-200 whitespace-pre-wrap">{fault.resolutionNotes}</p>
                )}
              </div>
            </Section>
          )}

          {/* Exit Actions */}
          {(fault.sentToBilling || fault.sentToDeals) && (
            <Section title="Exit Actions" icon={<Zap className="w-4 h-4" />} accentColor="text-amber-400" defaultOpen={false}>
              <div className="space-y-2">
                {fault.sentToBilling && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4 text-amber-400" />
                    <span className="text-white">Sent to Billing</span>
                    {fault.sentToBillingAt && <span className="text-xs text-slate-400">{formatDateTime(fault.sentToBillingAt)}</span>}
                  </div>
                )}
                {fault.sentToDeals && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="text-white">Sent to Deals</span>
                    {fault.sentToDealsAt && <span className="text-xs text-slate-400">{formatDateTime(fault.sentToDealsAt)}</span>}
                    {fault.linkedDealId && <span className="text-xs text-slate-400 font-mono">#{fault.linkedDealId.slice(0, 8)}</span>}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Related / Repeat Faults */}
          {fault.relatedFaults && fault.relatedFaults.length > 0 && (
            <Section
              title="Related Faults"
              icon={<Repeat className="w-4 h-4" />}
              count={fault.relatedFaults.length}
              defaultOpen={false}
              accentColor="text-red-400"
            >
              {fault.relatedFaults.map((rel, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-slate-400">#{rel.faultId.slice(0, 8)}</span>
                    <span className="text-slate-400">{formatDate(rel.createdAt)}</span>
                  </div>
                  <p className="text-slate-300">{rel.symptom}</p>
                  {rel.resolutionType && (
                    <span className="text-[10px] text-slate-400 uppercase">{rel.resolutionType.replace(/_/g, ' ')}</span>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* Monitoring */}
          {(fault.followUpDueDate || fault.followUpEmailDraft) && (
            <Section title="Monitoring" icon={<Mail className="w-4 h-4" />} accentColor="text-teal-400" defaultOpen={false}>
              <div className="space-y-2 text-xs">
                {fault.followUpDueDate && (
                  <div className="text-slate-300">
                    Due: {formatDateTime(fault.followUpDueDate)}
                    {fault.followUpCompleted && (
                      <span className="text-emerald-400 ml-2">
                        <CheckCircle className="w-3 h-3 inline mr-1" />
                        Completed {fault.followUpCompletedAt ? formatDateTime(fault.followUpCompletedAt) : ''}
                      </span>
                    )}
                  </div>
                )}
                {fault.followUpNotes && (
                  <p className="text-slate-200 whitespace-pre-wrap bg-white/5 rounded p-2">{fault.followUpNotes}</p>
                )}
                {fault.followUpEmailDraft && (
                  <div className="bg-white/5 border border-white/10 rounded p-2">
                    <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Email Draft</div>
                    <p className="text-slate-200 whitespace-pre-wrap">{fault.followUpEmailDraft}</p>
                    {fault.followUpEmailSentAt && (
                      <div className="text-emerald-400 mt-1">Sent: {formatDateTime(fault.followUpEmailSentAt)}</div>
                    )}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Timestamps Footer */}
          <div className="text-xs text-slate-500 pt-2 border-t border-white/10 space-y-0.5">
            <div>Created: {formatDateTime(fault.createdAt)}</div>
            <div>Updated: {formatDateTime(fault.updatedAt)}</div>
            {fault.archivedAt && <div>Archived: {formatDateTime(fault.archivedAt)}</div>}
            {fault.resolvedAt && <div>Resolved: {formatDateTime(fault.resolvedAt)}</div>}
            {fault.id && <div className="font-mono text-slate-600">ID: {fault.id}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
